// Story 1.19 — irreversible-action confirm modal for account deletion.
//
// Uses shadcn AlertDialog (UX spec §Modal patterns line 1858 — AlertDialog
// for destructive actions). Typed-DELETE confirmation per UX spec
// §Form Patterns line 1815 (Submit button states).
//
// On 422 ineligibility: surfaces the per-row checklist details to the
// parent page so the IneligibilityChecklist can render. On 409 already-
// pending: the parent page's TanStack invalidation will rerender the
// PendingDeletionPanel automatically.

import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Label,
} from "@greenhold/components";
import toast from "react-hot-toast";

import {
  useRequestDeletion,
  type DeletionError,
} from "./useRequestDeletion";

type DeleteAccountModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onIneligibility: (detail: DeletionError) => void;
};

export function DeleteAccountModal({
  open,
  onOpenChange,
  onIneligibility,
}: DeleteAccountModalProps) {
  const [confirmation, setConfirmation] = useState<string>("");
  const [inlineError, setInlineError] = useState<string | null>(null);
  const request = useRequestDeletion();
  // Depend on the stable `reset` fn, not the whole mutation object: a new
  // object identity every render + reset() notifying the observer =
  // "Maximum update depth". (This used to cite CashoutModal, which Story 8.3
  // deleted; the rationale is inlined so it does not rot again.)
  const { reset: resetRequest } = request;

  useEffect(() => {
    if (!open) {
      setConfirmation("");
      setInlineError(null);
      resetRequest();
    }
  }, [open, resetRequest]);

  const canSubmit = confirmation === "DELETE" && !request.isPending;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setInlineError(null);
    try {
      const result = await request.mutateAsync({
        body: { confirmation_text: "DELETE" },
        idempotencyKey: crypto.randomUUID(),
      });
      const date = new Date(result.scheduled_for);
      toast.success(
        `Deletion scheduled for ${date.toLocaleString()}. You can cancel within 24 hours.`,
      );
      onOpenChange(false);
    } catch (err) {
      const detail = err as DeletionError;
      if (detail.code === "ACCOUNT_DELETION_INELIGIBLE") {
        // Bubble the checklist details up to the page so the parent
        // can render IneligibilityChecklist with concrete numbers.
        onIneligibility(detail);
        setInlineError(
          detail.message ?? "Resolve the items below before scheduling deletion.",
        );
        return;
      }
      if (detail.code === "ACCOUNT_DELETION_ALREADY_PENDING") {
        // The invalidation on success would have refreshed the
        // status query; here it didn't fire because mutateAsync threw.
        // Just close the modal — the parent's useAccountDeletionStatus
        // poll will catch up shortly. Hint via toast for clarity.
        toast.success("A deletion request is already scheduled.");
        onOpenChange(false);
        return;
      }
      setInlineError(detail.message ?? "Deletion request failed. Try again.");
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete your Greenhold account?</AlertDialogTitle>
          <AlertDialogDescription>
            This is calm + deliberate, not a dark pattern. Read carefully.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-3 font-body text-sm">
          <p>Your account will be deleted.</p>
          <p>
            Your balance must be €0 — spend it on Greenhold, or refund a
            top-up that is still inside its 14-day window.
          </p>
          <p>
            Certificates already issued featuring you stay exactly as they
            are — collectors keep them, and your name and image remain on
            those certificates. This is permanent. You will not be featured
            in any new dossiers.
          </p>
          <p>
            Your audit-trail records are retained for 5 years per regulatory
            requirement.
          </p>
        </div>

        <div className="space-y-1">
          <Label htmlFor="deletion-confirm">Type DELETE to confirm</Label>
          <Input
            id="deletion-confirm"
            type="text"
            autoFocus
            autoComplete="off"
            value={confirmation}
            onChange={(e) => {
              setConfirmation(e.target.value);
              setInlineError(null);
            }}
            placeholder="DELETE"
          />
          <p className="text-xs text-muted-foreground">
            Case-sensitive — type the word in capital letters exactly.
          </p>
        </div>

        {inlineError && (
          <p className="text-sm text-destructive" role="alert">
            {inlineError}
          </p>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={request.isPending}>
            Keep my account
          </AlertDialogCancel>
          <Button
            type="button"
            variant="destructive"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {request.isPending ? "Submitting…" : "Delete my account"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
